/**
 * Mover a tabela dentro do documento do editor rico.
 *
 * Aparece uma alça no canto superior esquerdo quando o ponteiro entra na
 * tabela. Arrastando a alça:
 *
 * - **na vertical**, a tabela troca de posicao entre os blocos do documento,
 *   com uma linha marcando onde ela vai cair;
 * - **na horizontal**, a tabela desloca para a direita ou para a esquerda
 *   (`margin-left`), sem passar do zero.
 *
 * Tambem garante um paragrafo antes e depois de cada tabela: sem isso, uma
 * tabela no inicio ou no fim do conteudo deixa o usuario sem lugar para
 * digitar — problema classico de `contentEditable`.
 */

const DESLOCAMENTO_MAXIMO = 320

export function ativarMoverTabela(raiz: HTMLElement, aoMudar: () => void): () => void {
  const alca = document.createElement('div')
  alca.className = 'ob-rte-tablegrip'
  alca.contentEditable = 'false'
  alca.title = 'Arraste para mover a tabela — para os lados, desloca; para cima ou para baixo, muda de posição'
  alca.setAttribute('aria-hidden', 'true')
  alca.innerHTML = '<i class="fas fa-arrows-alt"></i>'
  alca.style.display = 'none'
  raiz.appendChild(alca)

  const marcador = document.createElement('div')
  marcador.className = 'ob-rte-dropline'
  marcador.contentEditable = 'false'
  marcador.style.display = 'none'
  raiz.appendChild(marcador)

  let tabelaAtiva: HTMLTableElement | null = null
  let arrastando = false
  let inicioX = 0
  let inicioY = 0
  let margemInicial = 0
  let destino: { bloco: HTMLElement; antes: boolean } | null = null

  /**
   * Lugares onde a tabela pode ser solta.
   *
   * Alem dos blocos de primeiro nivel, entram os `<li>` das listas: sem eles
   * nao havia como parar ENTRE topicos, so entre paragrafos. Soltar entre dois
   * `<li>` divide a lista em duas (ver `soltarEntre`), porque uma tabela nao
   * pode ficar dentro de um `<ul>`.
   */
  const blocos = () => {
    const saida: HTMLElement[] = []
    // Desce RECURSIVAMENTE nas listas: linha com recuo e uma lista dentro de
    // outra, e parando no primeiro nivel a tabela pulava esses itens inteiros.
    const percorrer = (pai: Element) => {
      for (const filho of Array.from(pai.children)) {
        if (!(filho instanceof HTMLElement) || filho === alca || filho === marcador) continue
        saida.push(filho)
        if (filho.tagName === 'UL' || filho.tagName === 'OL' || filho.tagName === 'LI') percorrer(filho)
      }
    }
    percorrer(raiz)
    return saida
  }

  /**
   * Insere a tabela na fresta escolhida.
   *
   * Quando o alvo e um `<li>`, a lista e partida: os itens antes ficam na
   * lista original, a tabela entra depois dela e os itens restantes vao para
   * uma lista nova, do mesmo tipo.
   */
  const soltarEntre = (tabela: HTMLTableElement, alvo: HTMLElement, antes: boolean) => {
    const lista = alvo.parentElement
    if (alvo.tagName !== 'LI' || !lista || (lista.tagName !== 'UL' && lista.tagName !== 'OL')) {
      if (antes) alvo.before(tabela)
      else alvo.after(tabela)
      return
    }

    const itens = Array.from(lista.children)
    const corte = itens.indexOf(alvo) + (antes ? 0 : 1)
    const restantes = itens.slice(corte)

    lista.after(tabela)
    if (restantes.length > 0) {
      const nova = document.createElement(lista.tagName.toLowerCase())
      for (const item of restantes) nova.appendChild(item)
      tabela.after(nova)
    }
    // a lista original pode ter ficado sem itens
    if (lista.children.length === 0) lista.remove()
  }

  let timerEsconder: ReturnType<typeof setTimeout> | null = null

  const posicionarAlca = (tabela: HTMLTableElement) => {
    if (timerEsconder) {
      clearTimeout(timerEsconder)
      timerEsconder = null
    }
    const area = tabela.getBoundingClientRect()
    const base = raiz.getBoundingClientRect()
    alca.style.display = 'flex'
    // encostada no canto superior esquerdo da tabela, POR CIMA dela: fora,
    // sobrava um vao entre a tabela e a alca e o ponteiro a perdia no caminho
    alca.style.top = `${area.top - base.top + raiz.scrollTop - 11}px`
    alca.style.left = `${area.left - base.left - 11}px`
  }

  /** Esconde com atraso: atravessar a borda nao pode fazer a alça sumir. */
  const esconderAlca = () => {
    if (arrastando || timerEsconder) return
    timerEsconder = setTimeout(() => {
      timerEsconder = null
      if (!arrastando) alca.style.display = 'none'
    }, 400)
  }

  const aoMover = (evento: MouseEvent) => {
    if (arrastando && tabelaAtiva) {
      const dx = evento.clientX - inicioX
      const dy = evento.clientY - inicioY

      // gesto horizontal domina: desloca sem trocar de posicao
      if (Math.abs(dx) > Math.abs(dy)) {
        const margem = Math.min(DESLOCAMENTO_MAXIMO, Math.max(0, margemInicial + dx))
        tabelaAtiva.style.marginLeft = `${margem}px`
        // a alça anda junto: ela e a pega da tabela, nao um botao solto
        posicionarAlca(tabelaAtiva)
        marcador.style.display = 'none'
        destino = null
      } else {
        // Vertical: escolhe o ponto de insercao MAIS PROXIMO do cursor, entre
        // todas as frestas do documento. Antes so marcava quando o ponteiro
        // caia dentro de um bloco, entao nos vaos a linha sumia e nao dava
        // para soltar ali.
        // Fronteiras equivalentes a posicao ATUAL da tabela sao descartadas:
        // "depois do irmao anterior" e "antes do irmao seguinte" e onde ela ja
        // esta, e soltar ali nao move nada. Sem isso o arraste para baixo
        // ficava preso nessas duas frestas.
        const anterior = tabelaAtiva.previousElementSibling
        const seguinte = tabelaAtiva.nextElementSibling

        const candidatos: { bloco: HTMLElement; antes: boolean; y: number }[] = []
        for (const bloco of blocos()) {
          if (bloco === tabelaAtiva) continue
          const area = bloco.getBoundingClientRect()
          if (bloco !== seguinte) candidatos.push({ bloco, antes: true, y: area.top })
          if (bloco !== anterior) candidatos.push({ bloco, antes: false, y: area.bottom })
        }

        let melhor: (typeof candidatos)[number] | null = null
        for (const candidato of candidatos) {
          if (!melhor || Math.abs(candidato.y - evento.clientY) < Math.abs(melhor.y - evento.clientY)) {
            melhor = candidato
          }
        }

        const base = raiz.getBoundingClientRect()
        if (melhor) {
          destino = { bloco: melhor.bloco, antes: melhor.antes }
          marcador.style.display = 'block'
          marcador.style.top = `${melhor.y - base.top + raiz.scrollTop}px`
        } else {
          destino = null
          marcador.style.display = 'none'
        }

        // A tabela so troca de lugar ao soltar, entao e a alça que acompanha o
        // ponteiro: sem isso ela ficava parada e o arraste nao parecia estar
        // levando a tabela junto.
        alca.style.top = `${evento.clientY - base.top + raiz.scrollTop - 11}px`
      }
      evento.preventDefault()
      return
    }

    // sem arraste: mostra a alça da tabela sob o ponteiro
    const alvo = evento.target instanceof HTMLElement ? evento.target.closest('table') : null
    if (alvo instanceof HTMLTableElement && raiz.contains(alvo)) {
      tabelaAtiva = alvo
      posicionarAlca(alvo)
      return
    }
    // sobre a propria alça: mantem visivel e cancela o desaparecimento
    if (alca.contains(evento.target as Node)) {
      if (timerEsconder) {
        clearTimeout(timerEsconder)
        timerEsconder = null
      }
      return
    }
    esconderAlca()
  }

  const aoPressionar = (evento: MouseEvent) => {
    if (!alca.contains(evento.target as Node) || !tabelaAtiva) return
    arrastando = true
    inicioX = evento.clientX
    inicioY = evento.clientY
    margemInicial = Number.parseFloat(tabelaAtiva.style.marginLeft || '0') || 0
    document.body.style.userSelect = ''
    document.body.style.cursor = 'grabbing'
    evento.preventDefault()
  }

  const aoSoltar = () => {
    if (!arrastando) return
    arrastando = false
    document.body.style.cursor = ''
    marcador.style.display = 'none'

    if (destino && tabelaAtiva) {
      soltarEntre(tabelaAtiva, destino.bloco, destino.antes)
      garantirParagrafosAoRedor(raiz)
    }
    // devolve a alça ao canto da tabela, onde quer que ela tenha parado
    if (tabelaAtiva) posicionarAlca(tabelaAtiva)
    destino = null
    aoMudar()
  }

  raiz.addEventListener('mousemove', aoMover)
  raiz.addEventListener('mousedown', aoPressionar)
  window.addEventListener('mousemove', aoMover)
  window.addEventListener('mouseup', aoSoltar)

  garantirParagrafosAoRedor(raiz)

  return () => {
    raiz.removeEventListener('mousemove', aoMover)
    raiz.removeEventListener('mousedown', aoPressionar)
    window.removeEventListener('mousemove', aoMover)
    window.removeEventListener('mouseup', aoSoltar)
    if (timerEsconder) clearTimeout(timerEsconder)
    alca.remove()
    marcador.remove()
    document.body.style.cursor = ''
  }
}

/**
 * Garante um paragrafo vazio antes e depois de cada tabela.
 *
 * Tabela colada no inicio ou no fim do conteudo nao deixa onde clicar para
 * digitar — o cursor nao tem para onde ir. O paragrafo resolve isso e some do
 * texto final por ser vazio.
 */
export function garantirParagrafosAoRedor(raiz: HTMLElement) {
  const paragrafo = () => {
    const p = document.createElement('p')
    p.appendChild(document.createElement('br'))
    return p
  }
  for (const tabela of Array.from(raiz.querySelectorAll('table'))) {
    if (tabela.parentElement !== raiz) continue
    const anterior = tabela.previousElementSibling
    if (!anterior || anterior.tagName === 'TABLE') tabela.before(paragrafo())
    const seguinte = tabela.nextElementSibling
    if (!seguinte || seguinte.tagName === 'TABLE') tabela.after(paragrafo())
  }
}
